import { Lead, Stage } from '../types';
import { getEffectiveStageIndexByScore } from './leadProcessing';
import { formatCurrency } from '@/shared/utils/formatting';

const nombres = [
  "Carlos & Ana García", "Diego & Camila Morales", "Carmen Vega", "Familia Ruiz-Silva", "Andrés & Valentina",
  "Familia Hernández", "Sofia & Chen Wang", "Familia López-Martín", "Roberto & Ana Kim", "Julián & Paola Sánchez",
  "Los Martínez", "Isabella & Miguel Torres", "Familia Jiménez", "Sandra & Luis Pérez", "Los Rodríguez-Luna",
  "Natalia & David Castro", "Familia Ortega", "Carolina & Felipe Gómez", "Los Valencia", "María & Juan Vargas",
  "Familia Restrepo", "Alejandra & Carlos Mora", "Los Aguilar", "Daniela & Sebastián Díaz", "Familia Muñoz",
  "Lucía & Pablo Herrera", "Los Delgado", "Valeria & Andrés Rojas", "Familia Ramírez", "Sofía & Mateo Cruz",
  "Los Guerrero", "Camila & Diego Medina", "Familia Vásquez", "Andrea & Nicolás Parra", "Los Silva-Moreno",
  "Gabriela & Eduardo León", "Familia Castaño", "Lorena & Ricardo Mendoza", "Los Giraldo", "Patricia & Javier Ramos",
  "Familia Cardona", "Diana & Alejandro Ruiz", "Los Ospina", "Claudia & Fernando Ríos", "Familia Montoya",
  "Tatiana & Mauricio Bedoya", "Los Franco", "Adriana & Gustavo Soto", "Familia Henao", "Marcela & Jorge Álvarez"
];

const apartamentos = [
  "Apt 1 Alcoba Torre Norte", "Apt 2 Alcobas Torre Sur", "Apt 3 Alcobas Torre Norte", "Penthouse Torre Sur",
  "Duplex Torre Norte", "Apt 1 Alcoba + Den", "Apt 2 Alcobas + Estudio", "Apt 3 Alcobas + Balcón",
  "Studio Torre Norte", "Loft Torre Sur", "Apt 2 Alcobas Vista Ciudad", "Apt 3 Alcobas Vista Montaña"
];

const profesiones = [
  "Ingeniero", "Médico", "Abogado", "Arquitecto", "Contador", "Profesor", "Empresario", "Desarrollador",
  "Gerente", "Consultor", "Director", "Analista", "Diseñador", "Vendedor", "Administrador"
];

const canales = [
  "Página web", "Referido", "Instagram", "Facebook Ads", "LinkedIn", "Google Ads", "Visita directa",
  "Feria inmobiliaria", "WhatsApp", "Llamada telefónica", "Email marketing"
];

const motivaciones = [
  "Primera vivienda", "Inversión", "Vivienda familiar", "Mejora de vivienda", "Inversión + Vivienda",
  "Vivienda de lujo", "Vivienda + Consultorio", "Pensión", "Herencia familiar"
];

export const stages: Stage[] = [
  { level: 1, label: "🆕 Nuevo", radius: 350, color: "rgba(34, 197, 94, 0.1)", mainColor: "#22C55E" },
  { level: 2, label: "💰 Cotización", radius: 250, color: "rgba(59, 130, 246, 0.1)", mainColor: "#3B82F6" },
  { level: 3, label: "🤝 Negociación", radius: 180, color: "rgba(249, 115, 22, 0.1)", mainColor: "#F97316" },
  { level: 4, label: "✅ Cierre", radius: 110, color: "rgba(168, 85, 247, 0.1)", mainColor: "#A855F7" }
];

export const stageMapping: Record<number, number> = {
  1: 1, // Captación → Nuevo
  2: 1, // Calificación → Nuevo
  3: 2, // Cotización → Cotización
  4: 3, // Negociación → Negociación
  5: 3, // Separación → Negociación
  6: 4, // Promesa → Cierre
  7: 4, // Construcción → Cierre
  8: 4  // Entrega → Cierre
};

export const stageSubsteps: Record<number, string[]> = {
  1: ['Registro Landing/Feria', 'Contacto Inicial', 'Calificación Inicial'],
  2: ['Validación de Datos', 'Identificación Necesidades', 'Segmentación VIS/No VIS'],
  3: ['Primera Cotización', 'Envío Info Adicional', 'Ajustes de Oferta'],
  4: ['Presentación Formal', 'Preaprobado Bancario', 'Validación Familiar'],
  5: ['Pago Separación 1-2%', 'Contrato Separación', 'Reserva en Inventario'],
  6: ['Elaboración Contrato', 'Firma Promesa', 'Vinculación Fiducia'],
  7: ['Pagos Periódicos', 'Reportes de Obra', 'Gestión de Cesión'],
  8: ['Escrituración', 'Pago Crédito/Leasing', 'Entrega Llaves + Posventa']
};

export const generateLeads = (): Lead[] => {
  const distribution = [12, 10, 8, 7, 6, 3, 2, 2]; // Total: 50 leads
  const leads: Lead[] = [];
  let leadId = 1;

  distribution.forEach((count, stageIndex) => {
    const stage = stageIndex + 1;
    const stageColors = ['#22C55E', '#EAB308', '#3B82F6', '#F97316', '#A855F7', '#9CA3AF', '#78716C', '#111827'];
    const stageStatuses = ['captacion', 'calificacion', 'cotizacion', 'negociacion', 'separacion', 'promesa', 'construccion', 'entrega'];

    for (let i = 0; i < count; i++) {
      const randomName = nombres[Math.floor(Math.random() * nombres.length)];
      const randomApto = apartamentos[Math.floor(Math.random() * apartamentos.length)];
      const randomProfesion = profesiones[Math.floor(Math.random() * profesiones.length)];
      const randomCanal = canales[Math.floor(Math.random() * canales.length)];
      const randomMotivacion = motivaciones[Math.floor(Math.random() * motivaciones.length)];

      const baseValue = 180000000 + Math.random() * 500000000;
      const ingresos = 3000000 + Math.random() * 25000000;

      let totalScore;
      let processed = false;

      if (stage <= 2) {
        // Para etapas tempranas (1-2), distribuir scores para que tengamos los 3 estados de calificación
        const distributionType = i % 3;
        if (distributionType === 0) {
          // Nuevos (sin procesar, score variable)
          totalScore = 20 + Math.random() * 60; // 20-80 (variable)
          processed = false;
        } else if (distributionType === 1) {
          // No calificado (score < 70)
          totalScore = 20 + Math.random() * 45; // 20-65
          processed = true;
        } else {
          // Calificado (score >= 70)
          totalScore = 70 + Math.random() * 25; // 70-95
          processed = true;
        }
      } else {
        // Para etapas avanzadas, mantener scores altos y todos procesados
        const stageBonus = stage * 5;
        const presupuesto = Math.min(95, 50 + Math.random() * 30 + stageBonus);
        const zona = 60 + Math.random() * 35;
        const datosCompletos = Math.min(100, 40 + Math.random() * 40 + stageBonus);
        const timing = 45 + Math.random() * 40;
        const credito = Math.min(95, 50 + Math.random() * 30 + stageBonus);
        totalScore = (presupuesto + zona + datosCompletos + timing + credito) / 5;
        processed = true;
      }

      // Generar scores individuales basados en el total
      const variation = 10;
      const presupuesto = Math.max(0, Math.min(100, totalScore + (Math.random() - 0.5) * variation));
      const zona = Math.max(0, Math.min(100, totalScore + (Math.random() - 0.5) * variation));
      const datosCompletos = Math.max(0, Math.min(100, totalScore + (Math.random() - 0.5) * variation));
      const timing = Math.max(0, Math.min(100, totalScore + (Math.random() - 0.5) * variation));
      const credito = Math.max(0, Math.min(100, totalScore + (Math.random() - 0.5) * variation));

      // Generar fechas realistas para createdAt y lastContact
      const daysAgo = Math.floor(Math.random() * 90); // Entre 0 y 90 días atrás
      const createdDate = new Date();
      createdDate.setDate(createdDate.getDate() - daysAgo);

      const lastContactDaysAgo = Math.floor(Math.random() * Math.min(daysAgo, 30)); // Entre 0 y 30 días desde creación
      const lastContactDate = new Date();
      lastContactDate.setDate(lastContactDate.getDate() - lastContactDaysAgo);

      const telefono = `+57 ${Math.floor(Math.random() * 900) + 300} ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 9000) + 1000}`;
      const email = `${randomName.toLowerCase().replace(/[^a-z]/g, '.')}@email.com`;

      // Calcular effectiveStageIndex basado en stage y score
      const effectiveStageIndex = getEffectiveStageIndexByScore(stage, Math.round(totalScore));

      leads.push({
        id: leadId.toString(),
        name: randomName,
        phone: telefono,
        email: email,
        unidadInteres: `${randomApto} Piso ${Math.floor(Math.random() * 15) + 3}`,
        value: Math.round(baseValue),
        stage,
        substage: Math.floor(Math.random() * 3) + 1,
        status: stageStatuses[stageIndex] as any,
        color: stageColors[stageIndex],
        size: 'mediano',
        progress: Math.floor(Math.random() * 99) + 1, // Progreso 1-99 para barras
        createdAt: createdDate.toISOString().split('T')[0], // Fecha de creación
        lastContact: lastContactDate.toISOString().split('T')[0], // Último contacto
        effectiveStageIndex: effectiveStageIndex, // Agregado para todos los leads
        scoring: {
          presupuesto: Math.round(presupuesto),
          zona: Math.round(zona),
          datosCompletos: Math.round(datosCompletos),
          timing: Math.round(timing),
          credito: Math.round(credito),
          total: Math.round(totalScore)
        },
        perfil: {
          telefono: telefono,
          email: email,
          profesion: randomProfesion,
          ingresos: Math.round(ingresos),
          miembros: Math.floor(Math.random() * 4) + 1,
          motivacion: randomMotivacion,
          fechaContacto: createdDate.toISOString().split('T')[0],
          canal: randomCanal
        },
        preferencias: {
          torre: Math.random() > 0.5 ? 'Norte' : 'Sur',
          piso: ['Alto', 'Medio', 'Bajo'][Math.floor(Math.random() * 3)],
          vista: ['Ciudad', 'Montaña', 'Interna'][Math.floor(Math.random() * 3)],
          parqueaderos: Math.floor(Math.random() * 3) + 1,
          deposito: Math.random() > 0.5,
          financiacion: ['Contado', 'Crédito', 'Leasing'][Math.floor(Math.random() * 3)]
        },
        qualification: {
          processed: processed,
          qualified: totalScore >= 70 && processed
        }
      });
      leadId++;
    }
  });

  return leads;
};

export const calculateLeadsPositions = (baseLeads: Lead[]): Lead[] => {
  return baseLeads.map((lead, index) => {
    const totalLeads = baseLeads.length;
    const startAngle = (index * 2 * Math.PI) / totalLeads;
    const mainStage = stageMapping[lead.stage] || 1;

    return {
      ...lead,
      startAngle,
      mainStage
    };
  });
};

export const getActionSuggestion = (status: string): string => {
  switch (status) {
    case 'captacion': return '📞 Llamar para calificar interés';
    case 'calificacion': return '📊 Validar datos y segmentar';
    case 'cotizacion': return '💰 Enviar cotización personalizada';
    case 'negociacion': return '🤝 Agendar presentación formal';
    case 'separacion': return '💳 Procesar pago separación';
    case 'promesa': return '📝 Firmar promesa compraventa';
    case 'construccion': return '🏗️ Enviar reporte de obra';
    case 'entrega': return '🔑 Coordinar escrituración';
    default: return 'Revisar estado prospecto';
  }
};

export const getCurrentSubstepName = (lead: Lead): string => {
  const substeps = stageSubsteps[lead.stage];
  if (!substeps || !lead.substage) return 'Paso inicial';

  const currentSubstep = substeps[lead.substage - 1];
  return currentSubstep || 'Paso actual';
};


export const getScoreColor = (score: number): string => {
  if (score >= 85) return '#10B981';
  else if (score >= 70) return '#F59E0B';
  else return '#EF4444';
};

export const getScoreLabel = (score: number): string => {
  if (score >= 85) return 'Alto';
  else if (score >= 70) return 'Medio';
  else return 'Bajo';
};
// Re-export formatting utilities
export { formatCurrency };
